import React from 'react';
import { Flex, Margin, WhiteSection } from 'components/base/layout-components';
import CycleTime from './components/CycleTime';
import ErrorBoundary from 'components/ErrorBoundary';
import Notifications from './components/Notifications';
import { TitleWithGraphIconV2 } from 'components/TitleWithIcons';
import { TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import {
  getCycleTimeConfigurations,
  goToBranchesOrderBy
} from 'views/root/views/home/views/dashboard/dashboard.service';
import DeliveryProgress from './components/DeliveryProgress/DeliveryProgress';
import useGoals from './hooks/useGoals';
import TeamGoals from './components/GoalsAndBenchmarks/Goals';
import { redirectToTeamGoals } from 'common/utils/utils';
import ContributorsRosterByTeamHidePersonalInfo from 'components/ContributorsRosterByTeamHidePersonalInfo/ContributorsRosterByTeamHidePersonalInfo';
import { useHistory } from 'react-router';
import { GOALS_PERIOD } from './teamDashboardCurrentIterationV2.service';
import WorkBreakdownNew from './components/WorkBreakdownNew/WorkBreakdownNew';
import {
  selectedTeamSelector,
  uiStateSelector
} from 'state/selectors/ui-state';
import { useSelector } from 'react-redux';
import { individualContributorsSelector } from 'state/selectors/contributors';
import {
  currentIterationSelector,
  isRetrospectSelector
} from 'state/selectors/iterations-picker';
import { myAccountSelector } from 'state/selectors/account';
import { teamsWithExtraSyncedDataSelector } from 'state/selectors/teams';
import MetricsWidgets from './components/MetricsWidgets/MetricsWidgets';
import { metricNames } from '@linearb/linjs-common';

const TeamDashboardCurrentIterationNew = ({
  handleOnSlackConnectClick,
  workBreakdownTrendData,
  teamWorkBreakdownData,
  cycleTimeTrendData,
  cycleTimeAverage,
  totalCycleTimeInMinutes,
  pipelineMilestones,
  notifications,
  teamSlackSettings,
  onRemoveNotificationClick,
  contributorsOrder,
  contributorsMetrics,
  setContributorsOrder,
  isNotificationsLoading,
  slackSensorAuhorization,
  isFinishedOnBoarding,
  issuesProps,
  isTeamPMConfigurationEnabled,
  isIterationsPeriodKeySelected,
  toggleModal,
  current5Iterations,
  isDefaultIterationTooLong,
  gitScopeToggle
}) => {
  const uiState = useSelector(uiStateSelector);
  const { dates, selectedTeamId } = uiState;
  const selectedTeam = useSelector(selectedTeamSelector) || {};
  const contributors = useSelector(individualContributorsSelector);
  const isRetrospect = useSelector(isRetrospectSelector);
  const currentIteration = useSelector(currentIterationSelector);
  const myAccount = useSelector(myAccountSelector);
  const currentTeams = useSelector(teamsWithExtraSyncedDataSelector);
  const history = useHistory();
  const { goals, teamDashboardId, teamDashboardWidgets, isGoalsDefaultPeriod } =
    useGoals({
      selectedTeamId: selectedTeam.id,
      gitScopeToggle
    });

  return (
    <Flex direction='column' width='100%'>
      {isFinishedOnBoarding && (
        <Flex>
          {/* Cycle Time */}
          <WhiteSection
            display='flex'
            borderRadius='0.45rem'
            height='22rem'
            mb='0.75rem'
            width='40%'
            data-testid='cycleTime-testid'
          >
            <ErrorBoundary componentName='Cycle Time'>
              <CycleTime
                toggleModal={toggleModal}
                value={totalCycleTimeInMinutes}
                trendData={cycleTimeTrendData}
                selectedTeamId={selectedTeamId}
                onLinkIconClick={() => {
                  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                    page: TRACKING.PAGES.DASHBOARD,
                    click_type: TRACKING.CLICK.CYCLE_TIME_CONTEXT
                  });
                  goToBranchesOrderBy(
                    'cycle_time,desc',
                    uiState,
                    selectedTeam,
                    history,
                    metricNames.CYCLE_TIME,
                    'cycle_time_updated'
                  );
                }}
                configurations={getCycleTimeConfigurations(pipelineMilestones)}
                avgTime={cycleTimeAverage}
                dates={dates}
              />
            </ErrorBoundary>
          </WhiteSection>

          {/* Work Breakdown and widgets */}
          <Flex height='22rem' width='60%'>
            <WorkBreakdownNew
              teamWorkBreakdownData={teamWorkBreakdownData}
              workBreakdownTrendData={workBreakdownTrendData}
            />
            <MetricsWidgets
              toggleModal={toggleModal}
              gitScopeToggle={gitScopeToggle}
            />
          </Flex>
        </Flex>
      )}

      <Flex>
        {/* ContributorsRosterByTeamHidePersonalInfo */}
        <WhiteSection
          mr='1rem'
          width='33%'
          borderRadius='0.45rem'
          height={isFinishedOnBoarding ? '51rem' : '73rem'}
          data-testid='hide-personal-information-table-testid'
        >
          <ErrorBoundary componentName='Team Members'>
            <ContributorsRosterByTeamHidePersonalInfo
              teams={currentTeams}
              selectedTeam={selectedTeam}
              order={contributorsOrder}
              contributorsMetrics={contributorsMetrics}
              onOrderChange={order => setContributorsOrder(order)}
              isRetrospect={isRetrospect}
              contributors={contributors}
              currentIteration={currentIteration}
              myAccount={myAccount}
              isFinishedOnBoarding={isFinishedOnBoarding}
            />
          </ErrorBoundary>
        </WhiteSection>

        {/* Delivery and Goals */}
        <Flex direction='column' width='33%'>
          <Margin r='small'>
            <Flex
              direction='column'
              items='start'
              backgroundColor='white'
              borderRadius='0.45rem'
              height={isFinishedOnBoarding ? '21rem' : '32rem'}
              padding='medium'
              data-cy='delivery-widgets'
            >
              <ErrorBoundary componentName='Delivery Progress'>
                <TitleWithGraphIconV2
                  title='Delivery'
                  subtitle={
                    !isIterationsPeriodKeySelected || isRetrospect
                      ? isDefaultIterationTooLong
                        ? '(Past 30 days)'
                        : `(Current Iteration)`
                      : ''
                  }
                />
                <Flex
                  height='100%'
                  width='100%'
                  items='center'
                  justify='center'
                >
                  <DeliveryProgress
                    isTeamPMConfigurationEnabled={isTeamPMConfigurationEnabled}
                    currentIteration={currentIteration}
                    issuesProps={issuesProps}
                  />
                </Flex>
              </ErrorBoundary>
            </Flex>
          </Margin>

          <Margin r='small' t='small'>
            <Flex
              backgroundColor='white'
              direction='column'
              items='start'
              borderRadius='0.45rem'
              height={isFinishedOnBoarding ? '21rem' : '32rem'}
              padding='medium'
              data-cy='goals-widgets'
            >
              <ErrorBoundary componentName='Goals'>
                <TitleWithGraphIconV2
                  onLinkIconClick={() => {
                    redirectToTeamGoals(history, selectedTeam, teamDashboardId);
                  }}
                  tooltipText='Go to Team Goals Dashboard'
                  title='Goals'
                  subtitle={
                    isGoalsDefaultPeriod ? `(Last ${GOALS_PERIOD} days)` : ''
                  }
                />
                <Flex
                  height='100%'
                  width='100%'
                  items='center'
                  justify='center'
                >
                  <TeamGoals
                    key='goals'
                    team={selectedTeam}
                    goals={goals}
                    teamDashboardWidgets={teamDashboardWidgets}
                  />
                </Flex>
              </ErrorBoundary>
            </Flex>
          </Margin>
        </Flex>

        {/* Notifications */}
        <WhiteSection
          width='34%'
          display='flex'
          borderRadius='0.45rem'
          height={isFinishedOnBoarding ? '51rem' : '73rem'}
        >
          <ErrorBoundary componentName='Notifications'>
            <Notifications
              slackChannel={
                teamSlackSettings.channel
                  ? teamSlackSettings.channel.name
                  : null
              }
              onSlackClick={
                teamSlackSettings.channel === null
                  ? handleOnSlackConnectClick
                  : null
              }
              newNotifications={
                notifications.filter(({ isNew }) => isNew).length
              }
              notifications={notifications}
              isLoading={isNotificationsLoading}
              onRemoveClick={onRemoveNotificationClick}
              history={history}
              sensorAuthorization={slackSensorAuhorization}
              selectedTeamDashboardId={teamDashboardId}
              selectedTeam={selectedTeam}
            />
          </ErrorBoundary>
        </WhiteSection>
      </Flex>
    </Flex>
  );
};

export default TeamDashboardCurrentIterationNew;
